import { Dayjs } from 'dayjs';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../dto/data-model.js';
import { MapUtils } from '../../utility/map-utils.js';
import { TimeZone } from '../../time/timezone.js';
import { Duration } from 'dayjs/plugin/duration.js';

export class RateLimiterLock extends DataModel {
  static readonly TYPE_ID: string = '39d041714cbb415a86939de18ed9ef1e';

  private constructor(
    public readonly uniqueId: string,
    public readonly expirationDate: Dayjs,
    public readonly lastAcquired: Dayjs,
    public readonly durationOverride: Duration | null,
  ) {
    super();
  }

  get $typeId(): string {
    return RateLimiterLock.TYPE_ID;
  }

  static create(args: {
    uniqueId: string,
    expirationDate: Dayjs,
    lastAcquired: Dayjs,
    durationOverride: Duration | null,
  }): RateLimiterLock {
    return new RateLimiterLock(
        args.uniqueId,
        args.expirationDate,
        args.lastAcquired,
        args.durationOverride,
    );
  }

  static acquireNew(uniqueId: string, now: Dayjs, duration: Duration): RateLimiterLock {
    return RateLimiterLock.create({
      uniqueId,
      expirationDate: now.add(duration),
      lastAcquired: now,
      durationOverride: null,
    });
  }

  acquire(now: Dayjs, duration: Duration): RateLimiterLock | null {
    const canAcquire = now.isAfter(this.expirationDate);
    if(!canAcquire) {
      return null;
    }

    return this.copyWith({
      expirationDate: now.add(this.durationOverride ?? duration),
      lastAcquired: now,
    });
  }

  static fromMap(
      context: SerializationContext,
      map: SerializedData,
  ): RateLimiterLock {
    return this.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      expirationDate: context.decodeDate(MapUtils.getNumber(map, 'expirationDate')),
      lastAcquired: context.decodeDate(MapUtils.getNumber(map, 'lastAcquired')),
      durationOverride: context.decodeDurationOrNull(MapUtils.getNumberOrNull(map, 'durationOverride')),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      uniqueId: this.uniqueId,
      expirationDate: context.encodeDateOrNull(this.expirationDate),
      exportationDateISO8601: this.expirationDate.toISOString(),
      lastAcquired: context.encodeDateOrNull(this.lastAcquired),
      lastAcquiredISO8601: this.lastAcquired.toISOString(),
      durationOverride: context.encodeDurationOrNull(this.durationOverride),
      durationOverrideISO8601: this.durationOverride?.toISOString() ?? null,
    };
  }

  clone(): this {
    return RateLimiterLock.create({
      uniqueId: this.uniqueId,
      expirationDate: TimeZone.cloneDate(this.expirationDate),
      lastAcquired: TimeZone.cloneDate(this.lastAcquired),
      durationOverride: this.durationOverride,
    }) as this;
  }

  copyWith({
    uniqueId = this.uniqueId,
    expirationDate = TimeZone.cloneDate(this.expirationDate),
    lastAcquired = TimeZone.cloneDate(this.lastAcquired),
    durationOverride = TimeZone.cloneDurationOrNull(this.durationOverride),
  }: {
    uniqueId?: string,
    expirationDate?: Dayjs,
    lastAcquired?: Dayjs,
    durationOverride?: Duration | null,
  } = {}): RateLimiterLock {
    return RateLimiterLock.create({
      uniqueId,
      expirationDate,
      lastAcquired,
      durationOverride,
    });
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
      other.expirationDate === this.expirationDate &&
      other.lastAcquired === this.lastAcquired &&
      other.durationOverride === this.durationOverride;
  }
}

export class RateLimiterLockBuilder extends DataModelBuilder<RateLimiterLock> {
  build(context: SerializationContext, map: SerializedData): RateLimiterLock {
    return RateLimiterLock.fromMap(context, map);
  }
}
